import { useEffect, useState } from "react";
import { Sprout, Save, ShieldCheck, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STY = {
  pending: { bg: "bg-amber-100", text: "text-amber-900", label: "Pending Approval" },
  approved: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Approved" },
  rejected: { bg: "bg-slate-200", text: "text-slate-600", label: "Rejected" },
};

export default function AdminAgriculturalGuidelines() {
  const [data, setData] = useState({ guidelines: [], uses: [], irrigation_categories: [], livestock_species: [], approval_statuses: [], assessment_classifications: [] });
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [filterUse, setFilterUse] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    const { data } = await api.get("/admin/agricultural-guidelines");
    setData(data);
    const ed = {};
    data.guidelines.forEach((g) => {
      ed[g.id] = { value_min: g.value_min, value_max: g.value_max, unit: g.unit, source: g.source, assessment_classification: g.assessment_classification, notes: g.notes || "" };
    });
    setEditing(ed);
  };
  useEffect(() => { load(); }, []);

  const approve = async (g) => {
    setSaving({ ...saving, [g.id]: true });
    try {
      await api.post(`/admin/agricultural-guidelines/${g.id}/approve`);
      toast.success(`${g.parameter} (${g.category || "universal"}) approved — now used for agricultural assessments`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not approve");
    } finally {
      setSaving({ ...saving, [g.id]: false });
    }
  };

  const reject = async (g) => {
    setSaving({ ...saving, [g.id]: true });
    try {
      await api.post(`/admin/agricultural-guidelines/${g.id}/reject`);
      toast.success(`${g.parameter} (${g.category || "universal"}) rejected — will not be used`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reject");
    } finally {
      setSaving({ ...saving, [g.id]: false });
    }
  };

  const save = async (g) => {
    const ed = editing[g.id];
    if (!ed) return;
    setSaving({ ...saving, [g.id]: true });
    try {
      await api.put(`/admin/agricultural-guidelines/${g.id}`, {
        value_min: ed.value_min === "" || ed.value_min === null ? null : Number(ed.value_min),
        value_max: ed.value_max === "" || ed.value_max === null ? null : Number(ed.value_max),
        unit: ed.unit, source: ed.source, assessment_classification: ed.assessment_classification, notes: ed.notes,
      });
      toast.success("Guideline saved — sent back to Pending Approval for re-review");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving({ ...saving, [g.id]: false });
    }
  };

  const visible = data.guidelines.filter((g) => (filterUse === "all" || g.intended_use === filterUse) && (filterStatus === "all" || g.approval_status === filterStatus));
  const pendingCount = data.guidelines.filter((g) => g.approval_status === "pending").length;

  return (
    <PortalShell title="Agricultural guideline library" subtitle="Admin · Irrigation & livestock water quality guidelines">
      <div className="mb-5 rounded-2xl bg-slate-900 text-white p-5 flex items-start gap-4">
        <Sprout className="w-5 h-5 text-cyan-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Agricultural suitability guidelines</div>
          <div className="text-sm mt-1">{data.guidelines.length} guideline rows · {pendingCount} pending approval</div>
          <p className="text-xs text-slate-300 mt-1.5">
            No guideline is ever used to classify a result until you approve it here. Starter values are seeded from ANZECC &amp; ARMCANZ 2000 as
            <b> Pending Approval</b> — review the source/value and approve, edit, or reject each one. Editing an already-approved value sends it back to
            Pending Approval automatically.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filterUse} onValueChange={setFilterUse}>
          <SelectTrigger data-testid="agguide-filter-use" className="w-48 h-10 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All intended uses</SelectItem>
            {data.uses.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="agguide-filter-status" className="w-48 h-10 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {data.approval_statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_STY[s]?.label || s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {visible.map((g) => {
          const ed = editing[g.id] || {};
          const sty = STATUS_STY[g.approval_status] || STATUS_STY.pending;
          return (
            <div key={g.id} data-testid={`agguide-row-${g.id}`} className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base font-semibold text-slate-900 capitalize">{g.parameter.replace(/_/g, " ")}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{g.intended_use}</span>
                    {g.category && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600 capitalize">{g.category.replace(/_/g, " ")}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sty.bg} ${sty.text}`}>{sty.label}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">{g.source} · {g.publication_version}</div>
                </div>
                <div className="flex items-center gap-2">
                  {g.approval_status !== "approved" && (
                    <Button data-testid={`agguide-approve-${g.id}`} size="sm" onClick={() => approve(g)} disabled={saving[g.id]} className="btn-aqua rounded-full">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  )}
                  {g.approval_status !== "rejected" && (
                    <Button data-testid={`agguide-reject-${g.id}`} size="sm" variant="outline" onClick={() => reject(g)} disabled={saving[g.id]} className="rounded-full">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Min value</Label>
                  <Input data-testid={`agguide-min-${g.id}`} type="number" step="any" value={ed.value_min ?? ""} onChange={(e) => setEditing({ ...editing, [g.id]: { ...ed, value_min: e.target.value } })} className="mt-1 h-9 rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Max value</Label>
                  <Input data-testid={`agguide-max-${g.id}`} type="number" step="any" value={ed.value_max ?? ""} onChange={(e) => setEditing({ ...editing, [g.id]: { ...ed, value_max: e.target.value } })} className="mt-1 h-9 rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Unit</Label>
                  <Input data-testid={`agguide-unit-${g.id}`} value={ed.unit ?? ""} onChange={(e) => setEditing({ ...editing, [g.id]: { ...ed, unit: e.target.value } })} className="mt-1 h-9 rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Source</Label>
                  <Input data-testid={`agguide-source-${g.id}`} value={ed.source ?? ""} onChange={(e) => setEditing({ ...editing, [g.id]: { ...ed, source: e.target.value } })} className="mt-1 h-9 rounded-lg text-xs" />
                </div>
                <div className="md:col-span-4">
                  <Label className="text-xs text-slate-600">Notes (shown internally only)</Label>
                  <Textarea data-testid={`agguide-notes-${g.id}`} value={ed.notes ?? ""} onChange={(e) => setEditing({ ...editing, [g.id]: { ...ed, notes: e.target.value } })} rows={2} className="mt-1 rounded-lg text-xs" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                  {g.approval_status === "approved" ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                  {(g.version_history || []).length ? `${g.version_history.length} prior edit${g.version_history.length === 1 ? "" : "s"} archived` : "No edits yet"}
                </div>
                <Button data-testid={`agguide-save-${g.id}`} size="sm" variant="outline" onClick={() => save(g)} disabled={saving[g.id]} className="rounded-full font-semibold">
                  <Save className="w-4 h-4 mr-1.5" /> Save
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </PortalShell>
  );
}
